// dateUtils.ts
import moment from "moment";
export function formatDateToMMDDYYYY(date: Date): string {
  try {
    date = new Date(date);
    const formattedDate = date.toLocaleDateString("en-us", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    return formattedDate.replace(/\//g, "/");
  } catch {
    return "N/A";
  }
}

export function formatDateToWords(date: Date): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  date = new Date(date);
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day}, ${year}`;
}

export function getTimeAgo(completionTime: Date | string): string {
  if (!completionTime) return "";

  // Get current time
  const now = moment();

  // Get timezone offset in minutes (e.g., +330 for IST which is +5:30)
  const timezoneOffsetMinutes = now.utcOffset();

  // Parse the completion time and add the timezone offset
  // const localTime = moment(completionTime).add(
  //   timezoneOffsetMinutes,
  //   "minutes"
  // );

  const localTime = moment.utc(completionTime).local();
  // Calculate difference in seconds
  const seconds = Math.abs(now.diff(localTime, "seconds"));

  // Check if the event occurred within 30 seconds
  if (seconds < 30) {
    return "Now";
  }

  // Check if the event occurred within the last hour
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  }

  // Check if the event occurred within the last 24 hours
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  }

  // If the event occurred more than 24 hours ago
  const daysDifference = Math.floor(seconds / 86400);

  if (daysDifference === 1) {
    return `Yesterday ${localTime.format("h:mm A")}`;
  }

  // For events older than 24 hours, format the date
  const currentYear = now.year();
  const eventYear = localTime.year();

  if (eventYear !== currentYear) {
    return localTime.format("MMMM D, YYYY");
  } else {
    return localTime.format("MMMM D");
  }
}

/**
 * Converts a UTC date string to a local timezone Date object
 * @param dateString - UTC date string (e.g., "2025-12-18T05:19:33.191813")
 * @returns Date object adjusted to local timezone
 */
export function convertUtcToLocalDate(dateString: Date | string | undefined | null): Date | null {
  if (!dateString) return null;
  
  try {
    // Get current time
    const now = moment();
    
    // Get timezone offset in minutes (e.g., +330 for IST which is +5:30)
    const timezoneOffsetMinutes = now.utcOffset();
    
    // Parse the UTC time and add the timezone offset
    const localTime = moment(dateString).add(
      timezoneOffsetMinutes,
      "minutes"
    );
    
    // Convert to JavaScript Date object
    return localTime.toDate();
  } catch (error) {
    console.error("Error converting UTC to local date:", error);
    return null;
  }
}

export function calculateDateProximity(date: Date): string {
  const newDate = new Date(date);
  const today = new Date();
  const endOfToday = new Date(today);
  endOfToday.setHours(23, 59, 59, 999);

  const endOfThisWeek = new Date(today);
  endOfThisWeek.setDate(endOfThisWeek.getDate() + (7 - endOfThisWeek.getDay()));

  const endOfThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  endOfThisMonth.setHours(23, 59, 59, 999);

  if (newDate >= today && newDate <= endOfToday) {
    return "Due Today";
  } else if (newDate > endOfToday && newDate <= endOfThisWeek) {
    return "Due Soon";
  } else if (newDate > endOfThisWeek && newDate <= endOfThisMonth) {
    return "Due This Month";
  } else if (newDate < today) {
    return "Overdue";
  } else {
    return "On Time";
  }
}

export const convertMinutesToHours = (minutes: number): string => {
  if (minutes === 0) {
    return "0";
  }

  const hours = minutes / 60;
  // Round to 2 decimal places
  const roundedHours = Math.round(hours * 100) / 100;
  // Convert to string with 2 decimal places, unless it's 0
  return roundedHours.toFixed(2).replace(/\.00$/, "");
};

export function getDateXDaysInFuture(days: number | undefined): string {
  const currentDate = new Date(); // Get current date
  if (days) {
    const futureDate = new Date(
      currentDate.getTime() + days * 24 * 60 * 60 * 1000
    ); // Calculate future date in milliseconds
    // Extract year, month, and day from futureDate
    const year = futureDate.getFullYear();
    const month = `${futureDate.getMonth() + 1}`.padStart(2, "0"); // Month is zero-based, so we add 1
    const day = `${futureDate.getDate()}`.padStart(2, "0");

    // Return formatted date string in 'yyyy-MM-dd' format
    return `${year}-${month}-${day}`;
  }
  return "";
}

export function formatDateToDateFormFormat(date: Date) {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
}

export function formatDateForInput(date: Date) {
  // Adjust for timezone offset
  const tzOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
  const localDate = new Date(date.getTime() - tzOffset);

  // Manually format the date components
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed, so add 1
  const day = String(localDate.getDate()).padStart(2, "0");
  const hours = String(localDate.getHours()).padStart(2, "0");
  const minutes = String(localDate.getMinutes()).padStart(2, "0");

  // Return the formatted string
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function formatDateTime(date: Date): string {
  // Get month, day, and year with proper zero-padding
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  // Determine AM/PM and convert hours to 12-hour format
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12; // the hour '0' should be '12'

  // Pad hours if necessary
  const hourStr = hours.toString().padStart(2, "0");

  // Return the formatted date string
  return `${month}/${day}/${year} ${hourStr}:${minutes} ${period}`;
}

// Format today's date however you want:
export function getTodayDateString(): string {
  const now = new Date();
  // e.g. "2025-01-31" or "1/31/2025"
  return now.toLocaleDateString();
  // or use e.g. `return now.toISOString().split("T")[0];`
}

export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const getMonthLabelFromNumber = (month: number): string => {
  return monthNames[month - 1] || "Invalid Month";
};

export const getMonthLabelFromMonthsAgo = (monthsAgo: number): string => {
  const today = new Date();

  console.log("How many months ago?", monthsAgo);
  // Adjust year and month properly to handle rollovers
  const targetDate = new Date(today);
  targetDate.setMonth(today.getMonth() - monthsAgo);
  console.log("Target Date: ", targetDate);

  return monthNames[targetDate.getMonth()] || "Invalid Month";
};

const DEBUG_SORTING = true;

/**
 * Parses a date value into a UTC timestamp (milliseconds since epoch).
 * This function is designed to be robust against common date string formats.
 * @param dateValue The date string or Date object.
 * @returns A number representing the UTC timestamp. Returns 0 for invalid or missing dates.
 */
export const getUTCTime = (dateValue: string | Date | undefined): number => {
  if (!dateValue) return 0;

  // If it's already a valid Date object, we can trust its internal value.
  if (dateValue instanceof Date) {
    return dateValue.getTime();
  }

  if (typeof dateValue === "string") {
    // Check for the problematic format from your REST API:
    // It's an ISO-like string that contains a 'T' but does NOT contain
    // a 'Z' (for UTC) or 'GMT' (for a specific timezone).
    if (
      dateValue.includes("T") &&
      !dateValue.includes("Z") &&
      !dateValue.includes("GMT")
    ) {
      // This is the fix: We append 'Z' to the string to explicitly
      // tell JavaScript "Treat this time as UTC, not local time."
      return new Date(dateValue + "Z").getTime();
    }

    // For all other string formats (like the full one from SignalR),
    // we can let the Date constructor parse them as it normally would.
    const date = new Date(dateValue);

    // Final check for any invalid date strings
    if (isNaN(date.getTime())) {
      console.warn(`Could not parse invalid date: "${dateValue}"`);
      return 0;
    }
    return date.getTime();
  }

  return 0; // Fallback for other types
};

export const getDifferenceBetweenTwoDates = (startDate : Date, endDate : Date) => {
  const oneDayInMilli = 1000 * 60 * 60 * 24;
  const oneHourInMilli = 1000 * 60 * 60;
  const oneMinInMilli = 1000 * 60;

  // 1) Get the time difference in milliseconds
  let dateDifference: number = Math.abs(endDate?.getTime() - startDate?.getTime());


  // 2) Get the Days difference between the two dates
  const days : number = Math.ceil(dateDifference / oneDayInMilli);
  dateDifference = dateDifference % oneDayInMilli;

  // 3) Get the Hours difference between the two dates
  const hours: number = Math.ceil(dateDifference / oneHourInMilli);
  dateDifference = dateDifference % oneHourInMilli;

  // 4) Get the minutes difference between the two dates
  const minutes: number =  Math.ceil(dateDifference / oneMinInMilli);

  return {days, minutes , hours};
}
